import React, { useRef, useEffect } from 'react'
import { useContent } from '../../contexts/ContentContext'
import { TESTIMONIALS_LIST } from '../../content/testimonials.list'
import SectionWrapper from '../ui/SectionWrapper'
import SectionLabel from '../ui/SectionLabel'
import LazyImage from '../ui/LazyImage'

/**
 * TestimonialsSection - Client testimonials with refined spiritual elegance
 *
 * DESIGN DIRECTION (April 2026):
 * - Mobile-first: Warm, inviting, meditation-like spacing
 * - Staggered animation: Testimonials surface like memories
 * - Portrait borders: Warm amber/gold accent for trust
 *
 * CARD ORDER (Mobile & Desktop):
 * 1. Name (portrait + name) + Stars (inline)
 * 2. Title/Context (role, location)
 * 3. Text (quote content)
 *
 * TYPOGRAPHY:
 * - Quote text: body-narrative token (1.125rem desktop, 1rem mobile)
 * - All cards use same typography for consistency
 * - Section label centered with subtitle underneath
 *
 * MOBILE LAYOUT:
 * - Featured: Full-width with gradient background
 * - Others: Clean cards in single column stack
 *
 * DESKTOP LAYOUT:
 * - Featured: Left column, full height (row-span-2)
 * - Others: 2-column grid on right
 */
export default function TestimonialsSection() {
  const { testimonials } = useContent()
  const [featured, ...others] = TESTIMONIALS_LIST
  const containerRef = useRef(null)

  // Staggered animation on mount
  useEffect(() => {
    const cards = containerRef.current?.querySelectorAll('[data-testid^="testimonial-card-"]')
    cards?.forEach((card, index) => {
      card.style.opacity = '0'
      card.style.transform = 'translateY(20px)'
      setTimeout(() => {
        card.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out'
        card.style.opacity = '1'
        card.style.transform = 'translateY(0)'
      }, index * 150)
    })
  }, [])

  return (
    <SectionWrapper id="testimonials" data-testid="testimonials-section">
      <div ref={containerRef}>
          {/* Centered section header */}
          <div className="text-center mb-8 md:mb-12">
            <SectionLabel text={testimonials.clientLabel} />
            <h2 className="typography-hero-mobile md:typography-hero text-center label-heading-spacing">
              {testimonials.clientSubtitle}
            </h2>
          </div>

          {/*
            Layout:
            Mobile (<768px) — Single column stack, featured with gradient
            Desktop (≥768px) — 3 columns: Featured (row-span-2) + 2-col grid
          */}
          <div className="content-spacing-lg grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-4 md:gap-6">

            {/* Featured testimonial — special treatment on mobile */}
            {featured && (
              <TestimonialCardFeatured
                testimonial={featured}
              />
            )}

            {/* Remaining testimonials — clean cards */}
            {others.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} index={index} />
            ))}

            {/* Empty slot(s) for desktop grid */}
            {others.length < 4 && (
              <div className="hidden md:block rounded-2xl border border-dashed border-color-light" />
            )}
          </div>
      </div>
    </SectionWrapper>
  )
}

/**
 * Featured testimonial card with gradient background
 * Mobile: Full-width with gradient
 * Desktop: Standard card styling with row-span-2
 *
 * ORDER: Name + Stars → Title → Text
 */
function TestimonialCardFeatured({ testimonial }) {
  return (
    <div
      className="md:row-span-2 relative overflow-hidden rounded-2xl p-6 md:p-8 border border-color-light/50 bg-gradient-to-br from-color-amber-50 via-white to-color-amber-100 md:bg-color-card-overlay"
      data-testid="testimonial-card-featured"
    >
      {/* Content */}
      <div className="relative z-10 flex flex-col min-h-[280px] md:min-h-0">
        {/* 1. Name (portrait + name) + Stars */}
        <div className="flex items-center gap-3 md:gap-4 mb-2">
          {testimonial.image && (
            <div className="relative">
              {/* Warm glow behind portrait - featured only */}
              <div className="absolute inset-0 bg-color-amber-300/30 rounded-full blur-md scale-110" />
              <LazyImage
                src={testimonial.image}
                alt={testimonial.name}
                className="relative w-12 h-12 md:w-14 md:h-14 rounded-full object-cover object-top border-2 border-color-amber-300 shrink-0"
              />
            </div>
          )}
          <div className="flex-1">
            <div className="font-primary text-xl font-semibold text-color-primary">
              {testimonial.name}
            </div>
          </div>
          {/* Stars inline with name */}
          <StarRating className="-ml-2" />
        </div>

        {/* 2. Title/Context */}
        <div className="mb-3">
          <div className="typography-label">
            {testimonial.context}
          </div>
        </div>

        {/* 3. Quote text — tighter leading on mobile so long quotes fit comfortably */}
        <blockquote className="typography-body-narrative-mobile md:typography-body-narrative !leading-snug md:!leading-[1.75]">
          {testimonial.quote}
        </blockquote>
      </div>
    </div>
  )
}

/**
 * Standard testimonial card with clean design
 * Same order as featured for consistency
 *
 * ORDER: Name + Stars → Title → Text
 */
function TestimonialCard({ testimonial, index }) {
  return (
    <div
      className="bg-color-card-overlay rounded-2xl p-6 md:p-8 border border-color-light/80 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col"
      data-testid={`testimonial-card-${index}`}
    >
      {/* 1. Name (portrait + name) + Stars */}
      <div className="flex items-center gap-3 mb-2">
        {testimonial.image && (
          <LazyImage
            src={testimonial.image}
            alt={testimonial.name}
            className="w-12 h-12 rounded-full object-cover border border-color-amber-200 shrink-0"
          />
        )}
        <div className="flex-1">
          <div className="font-primary text-xl font-semibold text-color-primary">
            {testimonial.name}
          </div>
        </div>
        {/* Stars inline with name */}
        <StarRating className="-ml-2" />
      </div>

      {/* 2. Title/Context */}
      <div className="mb-3">
        <div className="typography-label">
          {testimonial.context}
        </div>
      </div>

      {/* 3. Quote text */}
      <blockquote className="typography-body-narrative-mobile md:typography-body-narrative">
        {testimonial.quote}
      </blockquote>
    </div>
  )
}

/**
 * 5-star rating component
 * Uses color-star token from design system
 * Reusable across all testimonial cards
 */
function StarRating({ className = '' }) {
  return (
    <div className={`flex gap-1 ${className}`}>
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          className="w-5 h-5 text-color-star"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  )
}
